import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { PredictionCategory } from '../types/prediction'

export type Locale = 'zh-CN' | 'en'
export const DEFAULT_LOCALE: Locale = 'zh-CN'
const LOCALE_STORAGE_KEY = 'yujian:locale'

const zhCN = {
  'nav.today': '今日',
  'nav.changing': '判断在变',
  'nav.watch': '重点观察',
  'nav.finance': '财经',
  'nav.technology': 'AI 科技',
  'nav.world': '全球',
  'nav.sports': '体育',
  'nav.entertainment': '娱乐',
  'nav.crypto': '加密资产',
  'nav.search': '搜索',
  'nav.settings': '设置',
  'header.homeAria': '返回预见首页',
  'header.navAria': '主导航',
  'header.dataStatusTitle': '当前页面公开数据同步状态',
  'header.dataUpdated': '数据更新于 {time} · {status}',
  'header.syncUnknown': '同步状态未知',
  'header.searchAria': '搜索预测市场',
  'header.searchPlaceholder': '搜索市场',
  'header.submitSearch': '提交搜索',
  'header.languageAria': '选择页面语言',
  'header.chinese': '中文',
  'header.english': 'English',
  'footer.board': '预见 / 全球预测看板',
  'footer.sources': '公开源：Polymarket / Kalshi 最近同步 · CME FedWatch 未连接',
  'footer.complianceAria': '备案信息',
  'home.todayEyebrow': '今日判断',
  'home.todayTitle': '今天值得知道的判断',
  'home.todayDescription': '看概率怎么变，也看为什么值得关注。',
  'home.summaryAria': '首页摘要',
  'home.summaryCount': '条今天值得知道',
  'home.readingData': '正在读取公开数据',
  'home.browseEyebrow': '浏览预测 / {mode}',
  'home.categoryMode': '分类',
  'home.searchMode': '搜索',
  'home.browseDescription': '按信息价值、当前变化与来源整理，先看懂事件，再进入详情。',
  'home.backToday': '回到今天',
  'home.loadingEyebrow': '公开来源 / 读取中',
  'home.loadingTitle': '正在准备可读的预测信息',
  'home.loadingBody': '只使用当前已有的真实事件、概率、变化与来源。',
  'home.errorEyebrow': '公开来源 / 读取失败',
  'home.errorTitle': '暂时无法打开这组预测',
  'home.retry': '重新加载',
  'home.todaySectionEyebrow': '今日判断',
  'home.todaySectionTitle': '今天最值得知道',
  'home.todaySectionNote': '{count} 条 · 每条都有可核验的变化、截止时间或来源线索',
  'home.continueBrowsing': '继续浏览其余判断',
  'home.changingEyebrow': '24h / 概率变化',
  'home.changingTitle': '判断正在改变',
  'home.changingNote': '{count} 条 · 只展示有可核验变化的预测',
  'home.watchEyebrow': '长期观察',
  'home.watchTitle': '重点观察',
  'home.watchNote': '{count} 条 · 今天没有明显的新变化',
  'home.comparisonsEyebrow': '多个市场怎么看',
  'home.comparisonsTitle': '多平台怎么看',
  'home.comparisonsNote': '只展示正式 Catalog 中可核验的共识或分歧',
  'home.catalogEyebrow': '公开预测 / 可读结果',
  'home.recentlySynced': '公开市场最近同步数据',
  'home.searchResults': '搜索结果',
  'home.categoryPredictions': '{category}预测',
  'home.displayCount': '当前显示 {count} 条',
  'home.noMatches': '没有匹配的真实预测。可以换一个关键词或回到今天。',
  'home.noData': '暂无满足当前条件的真实数据，当前不补写条目。',
  'home.loadMoreLoading': '正在读取更多…',
  'home.loadMore': '加载更多预测',
  'home.tailDisclaimer': '概率是市场当前判断，不是事实、保证或官方预测。',
  'home.tailSource': '来源：Polymarket / Kalshi · 页面使用最近同步数据',
  'card.view': '查看：{title}',
  'card.currentJudgement': '当前判断',
  'card.happens': '发生什么',
  'card.todayWhy': '今天为什么值得看',
  'card.importance': '为什么重要',
  'card.changeNone': '暂无变化',
  'card.last24h': '过去24小时',
  'card.source': '来源',
  'card.deadline': '截止时间',
  'card.related': '相关预测',
  'card.probabilityMeaning': '概率含义',
  'card.marketJudgement': '市场当前判断',
  'card.noProbability': '暂无',
  'card.noCurrentProbability': '暂无当前概率',
  'card.noVerifiedChange': '当前没有可核验的新概率变化。',
  'card.ended': '已截止',
  'card.outcomeMulti': '多结果',
  'card.outcomeRange': '区间分布',
  'card.outcomeThreshold': '阈值分布',
  'card.outcomeSeries': '系列预测',
  'card.outcomeMutuallyExclusive': '互斥结果',
  'card.outcomeIndependent': '独立条件',
  'card.outcomeDistribution': '结果概率分布',
  'card.outcomeIndependentNote': '这些预测彼此独立，概率不一定合计100%。',
  'card.currentProbabilityLabel': '当前概率 {value}',
  'card.noVerifiableOutcomeProbability': '暂无可核验的结果概率',
  'card.highestOutcomeProbability': '最高结果概率 {value}',
  'card.relatedCount': '{count} 条',
  'detail.backToday': '回到今天',
  'detail.eyebrow': '预测详情 / {category}',
  'detail.ended': '已截止 · 以下为最近同步记录',
  'detail.currentMarketJudgement': '当前市场判断',
  'detail.currentMarketProbability': '当前市场概率',
  'detail.marketInterpretation': '市场把当前较高的概率分配给下列结果，但这不是事实或保证。',
  'detail.loadingEyebrow': '预测详情 / 读取中',
  'detail.loadingTitle': '正在准备这条预测',
  'detail.loadingBody': '只读取已有的公开概率、变化、来源与相关预测。',
  'detail.unavailableEyebrow': '预测详情 / 暂不可用',
  'detail.unavailableTitle': '这条预测暂时无法展示',
  'detail.notFound': '没有找到对应的公开事件。',
  'detail.missingId': '缺少事件标识。',
  'detail.readError': '暂时无法读取这条预测。',
  'detail.todaySignal': '01 / 今天的信号',
  'detail.todayWhy': '为什么今天值得看',
  'detail.noCompleteChange': '当前没有完整的 24h 双时间概率记录。',
  'detail.changeRecord': '24h 概率记录',
  'detail.informationValue': '02 / 信息价值',
  'detail.whyImportant': '为什么重要',
  'detail.publicSources': '03 / 公开来源',
  'detail.predictionSources': '预测来源',
  'detail.noCurrentProbability': '暂无当前概率',
  'detail.currentProbability': '当前概率：{value}%',
  'detail.viewSource': '查看来源 ↗',
  'detail.sourceNote': '{label}。这里展示各来源原始概率，不计算综合概率。',
  'detail.relatedEyebrow': '04 / 相关预测',
  'detail.relatedTitle': '同一事件的其他预测',
  'detail.relatedIntro': '这些是不同时间条件或结果条件的相关预测，不是重复数据。',
  'detail.probabilitySummary': '这个概率怎么看？',
  'detail.probabilityBody': '预测市场概率反映参与者当前交易形成的市场判断，不是事实、保证或官方预测。',
  'detail.resultTime': '结果时间',
  'detail.resultTimeBody': '概率会随着公开市场交易变化，页面只展示当前可核验记录。',
  'detail.dataBoundary': '数据边界',
  'detail.noSyntheticHistory': '不展示虚构历史曲线',
  'detail.dataBoundaryBody': '当前没有完整时间序列，因此只保留当前概率与真实 24h 记录。',
  'settings.eyebrow': '设置 / 数据源',
  'settings.title': '数据源中心',
  'settings.description': '管理公开预测市场的展示边界。连接信息只在当前浏览器保留，预见不会上传凭证或建立账户。',
  'settings.statusAria': '设置页状态',
  'settings.entry': '配置入口',
  'settings.localOnly': '仅保存在本机',
  'settings.saved': '已保存',
  'settings.unmodified': '未修改',
  'settings.publicSources': '公开来源',
  'settings.sourcesTitle': '预测来源',
  'settings.sourcesNote': '{count} 个来源 · 手动控制展示',
  'settings.systemBoundary': '系统边界',
  'settings.boundaryTitle': '只读取，不代替判断',
  'settings.boundaryBody': 'Polymarket 与 Kalshi 均使用最近一次成功同步的公开数据；页面不会在浏览器中直接请求源站实时数据。两者都不在这里进行交易或账户操作。',
  'settings.credentialPolicy': '凭证策略',
  'settings.storageLocation': '存储位置',
  'settings.browser': '当前浏览器',
  'settings.serverUpload': '服务器上传',
  'settings.notUpload': '不上传',
  'settings.loginAccount': '登录账户',
  'settings.notRequired': '不需要',
  'settings.configurationEyebrow': '本地配置 / {source}',
  'settings.configurationTitle': '配置连接预留',
  'settings.cmeDescription': 'CME FedWatch 当前只保留界面和接口配置，不会实际请求 CME API。',
  'settings.defaultSourceDescription': '默认公开源无需凭证；这里仅保留未来自定义接口的配置入口。',
  'settings.configurationBoundary': '仅限本机 · 本地存储 · 不发起 API 请求',
  'settings.apiBaseUrl': 'API Base URL（可选）',
  'settings.apiKey': 'API Key（可选）',
  'settings.apiSecret': 'API Secret（可选）',
  'settings.leaveBlank': '留空，暂不连接',
  'settings.localOnlyPlaceholder': '仅保存在当前浏览器',
  'settings.saveLocal': '保存到本地',
  'settings.close': '关闭',
  'settings.enablePublicData': '公开数据',
  'settings.footnote': '预见是静态站点。数据源开关、未来的 API 凭证与高级设置均只影响本浏览器的本地配置。',
  'source.predictionMarket': '预测市场',
  'source.financialData': '专业金融数据',
  'source.internalTest': '内部测试',
  'source.staticSync': '构建后静态同步数据',
  'source.staticSnapshot': '构建时静态快照',
  'source.reserved': '接口预留',
  'source.notPublic': '未用于公开展示',
  'source.connectedNotRequested': '已配置 · 未请求',
  'source.notConnected': '未连接',
  'source.syncedData': '已同步数据',
  'source.disabled': '已停用',
  'source.enabled': '已启用',
  'source.needsConfig': '需要用户配置',
  'source.noConfig': '无需配置',
  'source.noApiThisRun': '本轮不请求 API',
  'source.homeVisible': '首页可展示',
  'source.enableStateAria': '{source} 公开数据启用状态',
  'source.credentials': '凭证',
  'source.on': '开启',
  'source.off': '关闭',
  'source.configureConnection': '配置连接',
  'source.advancedSettings': '高级设置',
  'source.descPolymarket': '全球事件市场，默认使用最近一次成功同步的公开数据，无需配置即可使用。',
  'source.descKalshi': '受监管的事件市场，默认使用最近一次同步的公开数据。',
  'source.descCme': '美联储利率预期工具，本轮仅预留连接入口。',
  'source.descMock': '内部测试数据源，不用于公开展示。',
  'notFound.publicUnmatched': '公开市场 / 未匹配',
  'notFound.page404': '页面 404',
  'notFound.unobserved': '未观测',
  'notFound.eyebrow': '没有对应的公开预测',
  'notFound.title': '未观测到该事件',
  'notFound.body': '这条路径没有对应的公开预测市场，或者它已经离开当前观测范围。',
  'notFound.back': '返回今日预测',
  'common.synced': '已同步',
  'common.normalSync': '正常同步',
  'common.delayed': '同步延迟',
  'common.expired': '明显过期',
  'common.degraded': '同步异常 · 使用上次成功数据',
  'common.hidden': '已隐藏',
  'common.noData': '无有效数据',
  'common.unknown': '状态未知',
  'common.dataUpdated': '数据更新于 {time}',
  'common.syncDelayedLong': '同步延迟，数据可能不是最新',
  'common.expiredLong': '数据已过期，请勿视为当前判断',
  'common.degradedLong': '同步异常，使用上次成功数据',
  'common.noDataLong': '暂无有效同步数据',
  'common.deadlineUnknown': '截止时间未定',
  'common.timeUnknown': '时间未知',
  'common.ended': '已截止',
  'common.endedAt': '已截止 {time}',
  'common.deadline': '截止 {time}',
  'common.lessThanOneDay': '不到1天',
  'common.aboutOneDay': '约1天',
  'common.aboutDays': '约{days}天',
  'common.lessThanOnePoint': '不到1',
  'common.probabilityCrossed': '概率首次升破{value}%',
  'common.untilResolution': '距结果揭晓还有{time}',
  'common.lastSuccessfulSync': '最近成功同步 {time}',
  'common.noSuccessfulSync': '没有成功同步记录',
  'common.syncFailedUsingLast': '本次同步失败，使用上次成功数据',
  'common.currentlyShown': '{count} 条当前展示',
  'common.untitledPrediction': '未命名预测',
  'common.changeUnknown': '暂无可核验的 24h 变化',
  'common.changeUp': '过去24小时上升 {points} 个百分点',
  'common.changeDown': '过去24小时下降 {points} 个百分点',
  'common.changeFlat': '过去24小时没有变化',
  'common.todayWatch': '当前属于重点观察事件，今天没有明显的新概率变化。',
  'common.todayAdded': '今天新增预测',
  'common.todayNewSource': '今天出现新的预测来源',
  'common.marketHeat': '市场活跃度近期明显上升',
  'common.marketUpdated': '市场最近出现有效更新',
  'common.crossSource': '多个预测来源最近形成了新的判断',
  'common.marketClose': '市场判断接近',
  'common.marketDivergence': '两个市场存在明显分歧',
  'common.probabilityNoData': '当前没有可核验的市场概率。',
  'common.probabilityLow': '市场目前认为发生可能性较低。',
  'common.probabilityBelowAverage': '市场目前认为发生可能性偏低。',
  'common.probabilityUnclear': '市场判断仍较胶着。',
  'common.probabilityLikely': '市场目前偏向认为会发生。',
  'common.probabilityHigh': '市场目前认为发生可能性较高。',
  'importance.inflation': '通胀数据会影响利率判断，也会改变全球资金成本与资产定价。',
  'importance.growth': '经济增长数据反映所在经济体状态，也会影响全球市场的宏观判断。',
  'importance.rates': '利率与宏观政策会影响全球资金成本、汇率与主要资产定价。',
  'importance.chinaDirect': '直接涉及中国、台湾或香港，是理解中国相关外部变化的重要线索。',
  'importance.geopolitics': '涉及国家关系与安全局势，可能影响区域风险与政策走向。',
  'importance.aiCompany': '可能成为全球 AI 与科技产业的重要产品或资本市场事件。',
  'importance.ai': '反映 AI 产品与模型竞争的变化，会影响科技行业的预期。',
  'importance.market': '可能成为相关行业的重要资本市场事件。',
  'importance.crypto': '反映加密资产监管或行业变化，可能影响相关市场预期。',
  'importance.sports': '全球高关注体育事件，结果会引发广泛公众讨论。',
  'importance.entertainment': '反映高关注文化事件的公众讨论与行业动向。',
  'importance.topic': '这项预测聚焦{topic}，市场正在对具体结果形成判断。',
  'importance.category': '这项{category}预测围绕一个具体结果，适合直接理解当前市场判断。',
  'aria.probability': '概率 {value}',
} as const

export type MessageKey = keyof typeof zhCN

const en: Record<MessageKey, string> = {
  'nav.today': 'Today',
  'nav.changing': 'Moving',
  'nav.watch': 'Watchlist',
  'nav.finance': 'Finance',
  'nav.technology': 'AI / Tech',
  'nav.world': 'World',
  'nav.sports': 'Sports',
  'nav.entertainment': 'Culture',
  'nav.crypto': 'Crypto',
  'nav.search': 'Search',
  'nav.settings': 'Settings',
  'header.homeAria': 'Return to Yujian home',
  'header.navAria': 'Main navigation',
  'header.dataStatusTitle': 'Public data sync status for this page',
  'header.dataUpdated': 'Updated {time} · {status}',
  'header.syncUnknown': 'Sync status unknown',
  'header.searchAria': 'Search prediction markets',
  'header.searchPlaceholder': 'Search markets',
  'header.submitSearch': 'Submit search',
  'header.languageAria': 'Choose page language',
  'header.chinese': '中文',
  'header.english': 'English',
  'footer.board': 'Yujian / Global prediction board',
  'footer.sources': 'Public sources: Polymarket / Kalshi recently synced · CME FedWatch not connected',
  'footer.complianceAria': 'Registration information',
  'home.todayEyebrow': "Today's signals",
  'home.todayTitle': 'What is worth knowing today',
  'home.todayDescription': 'See how probabilities move, and why the change matters.',
  'home.summaryAria': 'Home summary',
  'home.summaryCount': 'signals worth knowing today',
  'home.readingData': 'Reading public data',
  'home.browseEyebrow': 'Explore predictions / {mode}',
  'home.categoryMode': 'category',
  'home.searchMode': 'search',
  'home.browseDescription': 'Sorted by information value, current movement, and source. Understand the event before opening its detail.',
  'home.backToday': 'Back to today',
  'home.loadingEyebrow': 'Public source / loading',
  'home.loadingTitle': 'Preparing readable prediction information',
  'home.loadingBody': 'Only current real events, probabilities, movements, and sources are used.',
  'home.errorEyebrow': 'Public source / failed',
  'home.errorTitle': 'This set of predictions is temporarily unavailable',
  'home.retry': 'Retry',
  'home.todaySectionEyebrow': "Today's signals",
  'home.todaySectionTitle': 'Most worth knowing today',
  'home.todaySectionNote': '{count} items · each has a verifiable movement, deadline, or source clue',
  'home.continueBrowsing': 'Continue through the remaining signals',
  'home.changingEyebrow': '24h / probability movement',
  'home.changingTitle': 'Judgements are moving',
  'home.changingNote': '{count} items · only predictions with verifiable movement',
  'home.watchEyebrow': 'Long-term watch',
  'home.watchTitle': 'Worth watching',
  'home.watchNote': '{count} items · no clear new movement today',
  'home.comparisonsEyebrow': 'How markets differ',
  'home.comparisonsTitle': 'Across platforms',
  'home.comparisonsNote': 'Only verifiable consensus or divergence from the formal Catalog',
  'home.catalogEyebrow': 'Public predictions / readable results',
  'home.recentlySynced': 'Recently synced public-market data',
  'home.searchResults': 'Search results',
  'home.categoryPredictions': '{category} predictions',
  'home.displayCount': '{count} currently shown',
  'home.noMatches': 'No real prediction matched. Try another keyword or return to today.',
  'home.noData': 'No real data meets the current conditions; no entries are invented.',
  'home.loadMoreLoading': 'Loading more…',
  'home.loadMore': 'Load more predictions',
  'home.tailDisclaimer': 'A probability is the market\'s current judgement, not a fact, guarantee, or official forecast.',
  'home.tailSource': 'Sources: Polymarket / Kalshi · page uses the latest synced data',
  'card.view': 'View: {title}',
  'card.currentJudgement': 'Current judgement',
  'card.happens': 'What happens',
  'card.todayWhy': 'Why look today',
  'card.importance': 'Why it matters',
  'card.changeNone': 'No movement',
  'card.last24h': 'Past 24 hours',
  'card.source': 'Source',
  'card.deadline': 'Deadline',
  'card.related': 'Related predictions',
  'card.probabilityMeaning': 'Probability meaning',
  'card.marketJudgement': 'Current market judgement',
  'card.noProbability': 'N/A',
  'card.noCurrentProbability': 'No current probability',
  'card.noVerifiedChange': 'No verifiable new probability movement.',
  'card.ended': 'Ended',
  'card.outcomeMulti': 'Multiple outcomes',
  'card.outcomeRange': 'Range distribution',
  'card.outcomeThreshold': 'Threshold distribution',
  'card.outcomeSeries': 'Series prediction',
  'card.outcomeMutuallyExclusive': 'Mutually exclusive outcomes',
  'card.outcomeIndependent': 'Independent conditions',
  'card.outcomeDistribution': 'Outcome distribution',
  'card.outcomeIndependentNote': 'These predictions are independent; probabilities do not necessarily add up to 100%.',
  'card.currentProbabilityLabel': 'Current probability {value}',
  'card.noVerifiableOutcomeProbability': 'No verifiable outcome probability',
  'card.highestOutcomeProbability': 'Highest outcome probability {value}',
  'card.relatedCount': '{count} items',
  'detail.backToday': 'Back to today',
  'detail.eyebrow': 'Prediction detail / {category}',
  'detail.ended': 'Ended · latest synced record shown below',
  'detail.currentMarketJudgement': 'Current market judgement',
  'detail.currentMarketProbability': 'Current market probability',
  'detail.marketInterpretation': 'The market assigns the higher current probability to the outcomes below; this is not a fact or guarantee.',
  'detail.loadingEyebrow': 'Prediction detail / loading',
  'detail.loadingTitle': 'Preparing this prediction',
  'detail.loadingBody': 'Only existing public probabilities, movements, sources, and related predictions are read.',
  'detail.unavailableEyebrow': 'Prediction detail / unavailable',
  'detail.unavailableTitle': 'This prediction cannot be shown right now',
  'detail.notFound': 'No matching public event was found.',
  'detail.missingId': 'The event identifier is missing.',
  'detail.readError': 'This prediction could not be read right now.',
  'detail.todaySignal': '01 / Today\'s signal',
  'detail.todayWhy': 'Why look today',
  'detail.noCompleteChange': 'There is no complete pair of 24h probability records.',
  'detail.changeRecord': '24h probability record',
  'detail.informationValue': '02 / Information value',
  'detail.whyImportant': 'Why it matters',
  'detail.publicSources': '03 / Public sources',
  'detail.predictionSources': 'Prediction sources',
  'detail.noCurrentProbability': 'No current probability',
  'detail.currentProbability': 'Current probability: {value}%',
  'detail.viewSource': 'View source ↗',
  'detail.sourceNote': '{label}. Raw source probabilities are shown here; no combined probability is calculated.',
  'detail.relatedEyebrow': '04 / Related predictions',
  'detail.relatedTitle': 'Other predictions in this event',
  'detail.relatedIntro': 'These are related predictions with different time or outcome conditions, not duplicates.',
  'detail.probabilitySummary': 'How should I read this probability?',
  'detail.probabilityBody': 'Prediction-market probability reflects the current judgement formed by trading participants, not a fact, guarantee, or official forecast.',
  'detail.resultTime': 'Resolution time',
  'detail.resultTimeBody': 'Probabilities move with public-market trading; this page shows only currently verifiable records.',
  'detail.dataBoundary': 'Data boundary',
  'detail.noSyntheticHistory': 'No fabricated historical curve',
  'detail.dataBoundaryBody': 'There is no complete time series, so only the current probability and real 24h record are retained.',
  'settings.eyebrow': 'Settings / data sources',
  'settings.title': 'Data source center',
  'settings.description': 'Manage the display boundary for public prediction markets. Connection details stay in this browser; Yujian does not upload credentials or create accounts.',
  'settings.statusAria': 'Settings page status',
  'settings.entry': 'Configuration entry',
  'settings.localOnly': 'Stored on this device only',
  'settings.saved': 'Saved',
  'settings.unmodified': 'Unmodified',
  'settings.publicSources': 'Public sources',
  'settings.sourcesTitle': 'Prediction sources',
  'settings.sourcesNote': '{count} sources · control display manually',
  'settings.systemBoundary': 'System boundary',
  'settings.boundaryTitle': 'Read only, never replace judgement',
  'settings.boundaryBody': 'Polymarket and Kalshi use their latest successful public sync. The page does not request live source data in the browser. Neither trading nor account operations happen here.',
  'settings.credentialPolicy': 'Credential policy',
  'settings.storageLocation': 'Storage',
  'settings.browser': 'This browser',
  'settings.serverUpload': 'Server upload',
  'settings.notUpload': 'Not uploaded',
  'settings.loginAccount': 'Login account',
  'settings.notRequired': 'Not required',
  'settings.configurationEyebrow': 'Local configuration / {source}',
  'settings.configurationTitle': 'Reserved connection configuration',
  'settings.cmeDescription': 'CME FedWatch keeps only the interface and configuration entry in this phase; no CME API request is made.',
  'settings.defaultSourceDescription': 'Default public sources need no credentials; this entry is reserved for a future custom endpoint.',
  'settings.configurationBoundary': 'This device only · local storage · no API request',
  'settings.apiBaseUrl': 'API Base URL (optional)',
  'settings.apiKey': 'API Key (optional)',
  'settings.apiSecret': 'API Secret (optional)',
  'settings.leaveBlank': 'Leave blank; not connected yet',
  'settings.localOnlyPlaceholder': 'Stored in this browser only',
  'settings.saveLocal': 'Save locally',
  'settings.close': 'Close',
  'settings.enablePublicData': 'Public data',
  'settings.footnote': 'Yujian is a static site. Data-source switches, future API credentials, and advanced settings affect only local configuration in this browser.',
  'source.predictionMarket': 'Prediction market',
  'source.financialData': 'Professional financial data',
  'source.internalTest': 'Internal test',
  'source.staticSync': 'Post-build static sync data',
  'source.staticSnapshot': 'Build-time static snapshot',
  'source.reserved': 'Interface reserved',
  'source.notPublic': 'Not used for public display',
  'source.connectedNotRequested': 'Configured · not requested',
  'source.notConnected': 'Not connected',
  'source.syncedData': 'Synced data',
  'source.disabled': 'Disabled',
  'source.enabled': 'Enabled',
  'source.needsConfig': 'User configuration required',
  'source.noConfig': 'No configuration required',
  'source.noApiThisRun': 'No API request in this phase',
  'source.homeVisible': 'Visible on home',
  'source.enableStateAria': '{source} public-data enabled state',
  'source.credentials': 'Credentials',
  'source.on': 'On',
  'source.off': 'Off',
  'source.configureConnection': 'Configure connection',
  'source.advancedSettings': 'Advanced settings',
  'source.descPolymarket': 'A global event market using the latest successful public sync by default; no configuration is required.',
  'source.descKalshi': 'A regulated event market using the latest synced public data by default.',
  'source.descCme': 'A Federal Reserve rate-expectations tool; this phase reserves only the connection entry.',
  'source.descMock': 'An internal test source that is not used for public display.',
  'notFound.publicUnmatched': 'Public market / unmatched',
  'notFound.page404': 'Page 404',
  'notFound.unobserved': 'Unobserved',
  'notFound.eyebrow': 'No matching public prediction',
  'notFound.title': 'This event was not observed',
  'notFound.body': 'This path has no corresponding public prediction market, or it has left the current observation range.',
  'notFound.back': 'Return to today\'s predictions',
  'common.synced': 'Synced',
  'common.normalSync': 'Normal sync',
  'common.delayed': 'Sync delayed',
  'common.expired': 'Clearly stale',
  'common.degraded': 'Sync issue · using last successful data',
  'common.hidden': 'Hidden',
  'common.noData': 'No valid data',
  'common.unknown': 'Status unknown',
  'common.dataUpdated': 'Updated {time}',
  'common.syncDelayedLong': 'Sync delayed; data may not be current',
  'common.expiredLong': 'Data is stale; do not treat it as a current judgement',
  'common.degradedLong': 'Sync issue; using the last successful data',
  'common.noDataLong': 'No valid synced data',
  'common.deadlineUnknown': 'Deadline unknown',
  'common.timeUnknown': 'Unknown time',
  'common.ended': 'Ended',
  'common.endedAt': 'Ended {time}',
  'common.deadline': 'Deadline {time}',
  'common.lessThanOneDay': 'Less than 1 day',
  'common.aboutOneDay': 'About 1 day',
  'common.aboutDays': 'About {days} days',
  'common.lessThanOnePoint': 'less than 1',
  'common.probabilityCrossed': 'Probability first crossed {value}%',
  'common.untilResolution': 'About {time} until resolution',
  'common.lastSuccessfulSync': 'Last successful sync {time}',
  'common.noSuccessfulSync': 'No successful sync recorded',
  'common.syncFailedUsingLast': 'This sync failed; using the last successful data',
  'common.currentlyShown': '{count} currently shown',
  'common.untitledPrediction': 'Untitled prediction',
  'common.changeUnknown': 'No verifiable 24h movement',
  'common.changeUp': 'Up {points} percentage points in 24h',
  'common.changeDown': 'Down {points} percentage points in 24h',
  'common.changeFlat': 'No change in 24h',
  'common.todayWatch': 'A watchlist event with no clear new probability movement today.',
  'common.todayAdded': 'New prediction today',
  'common.todayNewSource': 'A new prediction source appeared today',
  'common.marketHeat': 'Recent market activity has increased clearly',
  'common.marketUpdated': 'The market received a recent valid update',
  'common.crossSource': 'Multiple prediction sources formed a new judgement recently',
  'common.marketClose': 'Markets are close',
  'common.marketDivergence': 'The two markets diverge clearly',
  'common.probabilityNoData': 'There is no verifiable market probability right now.',
  'common.probabilityLow': 'The market currently sees a low likelihood of occurrence.',
  'common.probabilityBelowAverage': 'The market currently leans below even odds.',
  'common.probabilityUnclear': 'The market judgement remains close to evenly split.',
  'common.probabilityLikely': 'The market currently leans toward occurrence.',
  'common.probabilityHigh': 'The market currently sees a high likelihood of occurrence.',
  'importance.inflation': 'Inflation data changes rate expectations, global funding costs, and asset pricing.',
  'importance.growth': 'Growth data describes the state of an economy and influences the global macro view.',
  'importance.rates': 'Rates and macro policy affect global funding costs, currencies, and major asset prices.',
  'importance.chinaDirect': 'It directly involves China, Taiwan, or Hong Kong and is a useful signal for China-related external change.',
  'importance.geopolitics': 'It concerns international relations and security, which may affect regional risk and policy direction.',
  'importance.aiCompany': 'It may become an important product or capital-market event for global AI and technology.',
  'importance.ai': 'It reflects competition among AI products and models and may shift technology-sector expectations.',
  'importance.market': 'It may become an important capital-market event for the sector.',
  'importance.crypto': 'It reflects regulatory or industry change in crypto assets and may shift related market expectations.',
  'importance.sports': 'It is a high-attention global sports event likely to drive broad public discussion.',
  'importance.entertainment': 'It reflects public discussion and industry movement around a high-attention cultural event.',
  'importance.topic': 'This prediction focuses on {topic}, where the market is forming a view on a concrete outcome.',
  'importance.category': 'This {category} prediction concerns a concrete outcome and makes the current market view easier to understand.',
  'aria.probability': 'Probability {value}',
}

const dictionaries: Record<Locale, Record<MessageKey, string>> = { 'zh-CN': zhCN, en }

export function isLocale(value: unknown): value is Locale {
  return value === 'zh-CN' || value === 'en'
}

export function categoryLabel(category: PredictionCategory, locale: Locale = DEFAULT_LOCALE) {
  const labels: Record<PredictionCategory, MessageKey> = {
    finance: 'nav.finance',
    technology: 'nav.technology',
    world: 'nav.world',
    sports: 'nav.sports',
    entertainment: 'nav.entertainment',
    crypto: 'nav.crypto',
  }
  return dictionaries[locale][labels[category]]
}

export function tForLocale(locale: Locale, key: MessageKey, values: Record<string, string | number> = {}) {
  return dictionaries[locale][key].replace(/\{(\w+)\}/g, (_, name: string) => String(values[name] ?? `{${name}}`))
}

interface LocaleContextValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: MessageKey, values?: Record<string, string | number>) => string
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

function initialLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY)
    return isLocale(stored) ? stored : DEFAULT_LOCALE
  } catch {
    return DEFAULT_LOCALE
  }
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(initialLocale)

  useEffect(() => {
    document.documentElement.lang = locale
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
    } catch {
      // Storage may be unavailable in a restricted browser context.
    }
  }, [locale])

  const translate = useCallback((key: MessageKey, values: Record<string, string | number> = {}) => tForLocale(locale, key, values), [locale])
  const value = useMemo(() => ({ locale, setLocale, t: translate }), [locale, translate])
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale() {
  const value = useContext(LocaleContext)
  if (!value) throw new Error('useLocale must be used inside LocaleProvider')
  return value
}
